import { useEffect, useRef } from "react";
import { apiService } from "@/services";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import queryClient from "@/query-client";
import type { GetStrategyRunResponse } from "@/types";
import { isRunInFlight, RUN_POLL_INTERVAL_MS } from "@/utils/landscapeRunStatus";
import { getSeoStrategyRecommendationsQueryPrefix } from "@/resources/seo-strategy/seo-strategy.api";

/**
 * `GET /api/products/hero/{id}/strategy-run`
 * (`components/work-run-log.md § GET /api/products/hero/{id}/strategy-run`,
 * `WEB-002`). The contract mirror lives in `src/types/strategy-run.ts` --
 * this module imports it and declares no shape of its own
 * (`conventions/08-api-contracts.md`). Poll interval and the
 * queued/running/stopped predicate come from the shared work-run vocabulary
 * (`src/utils/landscapeRunStatus.ts`) rather than a second copy.
 *
 * This is a poll, not a broadcast (`constraints.md § C-8`, fifth named case):
 * the module never references `echo.service` or an SEO-strategy broadcast
 * event. Like `keyword-bank-run.api.ts` it owns one cross-query invalidation
 * (AUT-196 PR review): the SEO-strategy recommendations query has a 5-minute
 * staleTime and `refetchOnWindowFocus` off, so when the run settles the
 * already-written recommendations do not appear until reload. On the
 * in-flight -> ended transition this module invalidates that list once so the
 * tab flips from "Building your strategy" to the real recommendations.
 */

// Distinct from the SEO-strategy recommendations key
// (`["seo-strategy-recommendations", …]`, `src/resources/seo-strategy/seo-strategy.api.ts`)
// so invalidating one never refetches the other.
const strategyRunQueryKeyPrefix = ["product", "strategy-run"] as const;

export const strategyRunQueryKey = (productId: number) =>
  [...strategyRunQueryKeyPrefix, String(productId)] as const;

// Below RUN_POLL_INTERVAL_MS, the same margin `keyword-bank-run.api.ts` uses
// -- otherwise a background focus/reconnect refetch could be suppressed as
// "still fresh" for up to the poll interval, which would defeat FR-6's
// "sees the state change without pressing anything".
const STRATEGY_RUN_STALE_TIME_MS = 10_000;

/**
 * The poll predicate (FR-6, NFR-1): poll while the loaded run's reported
 * status is `queued`, `running` or `stopped`; stop on every ending and when
 * there is no run at all. A separate exported function so it is directly
 * unit-testable without inspecting the registered `refetchInterval`
 * callback.
 *
 * `data.run === null` is its own branch, not folded into the `!data`
 * check above it: every hero product added before this ships has no run,
 * so a predicate that polled on `null` would poll forever, on the majority
 * of products, for a row that will never exist.
 */
export function computeStrategyRunRefetchInterval(
  data: GetStrategyRunResponse | undefined,
): number | false {
  if (!data) return false;
  if (data.run === null) return false;
  return isRunInFlight(data.run.status) ? RUN_POLL_INTERVAL_MS : false;
}

/**
 * `useGetStrategyRun(productId)` -- the React Query result, unchanged.
 * `data` is `GetStrategyRunResponse | undefined`, so a consumer's
 * `data?.run` is `StrategyRun | null | undefined`: *not loaded / errored*
 * versus *never run* are two different things (`WEB-003` branches on both),
 * and this hook does not collapse them -- it does not normalise `undefined`
 * to `null`, does not return a bare `run`, and does not swallow `isError`
 * into a `null` run.
 *
 * `apiService` turns a non-401 failure into a thrown `ApiError`, and
 * `query-client.ts` retries only `isNetworkError || status >= 500`. That is
 * exactly why the endpoint answers `200 {"run": null}` instead of `404`;
 * this hook adds no error branch that would re-create the failure mode the
 * endpoint was shaped to avoid.
 *
 * Sends no `brand_id` -- a strategy run is scoped by product id alone.
 */
export function useGetStrategyRun(productId: number): UseQueryResult<GetStrategyRunResponse> {
  const query = useQuery<GetStrategyRunResponse>({
    queryKey: strategyRunQueryKey(productId),
    queryFn: () => apiService.get<GetStrategyRunResponse, undefined>(`/products/hero/${productId}/strategy-run`),
    staleTime: STRATEGY_RUN_STALE_TIME_MS,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    // Reads `query.state.data`, which TanStack v5 keeps populated with the
    // last successfully loaded value across a failed background refetch --
    // so a refresh that errors neither blanks the tab nor stops the poll
    // it was itself supposed to keep alive.
    refetchInterval: (q) => computeStrategyRunRefetchInterval(q.state.data),
    // refetchIntervalInBackground left at the React Query default (false):
    // a page holding a permanently `stopped` run would otherwise poll
    // forever, including in a backgrounded tab.
  });

  // AUT-196: on the in-flight -> ended transition, invalidate the SEO-strategy
  // recommendations list once so the tab shows the freshly-written
  // recommendations without a reload. This hook holds only the product id, not
  // the ASIN the list keys on, so it invalidates the whole list prefix (a
  // superset of the active ASIN's key) rather than a divergent hardcoded key.
  // `isRunInFlight` (queued/running/stopped), matching the poll predicate,
  // means a run ending in ANY terminal status triggers the invalidation. A
  // `sawInFlight` ref -- not a comparison against the previous poll's status --
  // keeps this from firing on `queued -> running`, on every poll of a run that
  // stays in flight, or on a first response that already carries an ending for
  // a run this client never saw in flight.
  const sawInFlight = useRef(false);

  useEffect(() => {
    const run = query.data?.run;
    if (run === undefined || run === null) return;

    if (isRunInFlight(run.status)) {
      sawInFlight.current = true;
      return;
    }

    if (sawInFlight.current) {
      sawInFlight.current = false;
      queryClient.invalidateQueries({ queryKey: getSeoStrategyRecommendationsQueryPrefix() });
    }
  }, [query.data]);

  return query;
}
